import AdminLayout from "@/components/layout-admin";
import { useApartments, useCreateApartment, useUpdateApartment, useDeleteApartment, useBuildings, useAdminUsers, useQaTemplates, useApplyQaTemplate } from "@/hooks/use-resources";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Building, Settings, Pencil, Trash2, RefreshCw, Copy, User, Search, X, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertApartmentSchema, type Apartment } from "@shared/schema";
import { z } from "zod";
import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useSearch } from "wouter";
import { SearchableSelect } from "@/components/ui/searchable-select";

const formSchema = insertApartmentSchema.extend({
  buildingId: z.coerce.number().min(1, "Select a building"),
});

type ApartmentFormValues = z.infer<typeof formSchema>;

function ApartmentForm({
  defaultValues,
  onSubmit,
  isPending,
  submitLabel,
  buildings,
  owners,
}: {
  defaultValues: ApartmentFormValues;
  onSubmit: (values: ApartmentFormValues) => void;
  isPending: boolean;
  submitLabel: string;
  buildings: { id: number; name: string }[];
  owners: { id: number; fullName: string | null; username: string }[];
}) {
  const form = useForm<ApartmentFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name (e.g., Unit 101)</FormLabel>
                <FormControl><Input {...field} data-testid="input-apt-name" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-1.5">
                  <FormLabel>Slug (URL friendly)</FormLabel>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help mb-1" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        A unique URL-safe identifier for this property (e.g. "unit-101-miami"). Used in guest portal links and the booking engine URL.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <FormControl><Input placeholder="unit-101-miami" {...field} data-testid="input-apt-slug" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="buildingId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Building</FormLabel>
              <FormControl>
                <SearchableSelect
                  value={field.value?.toString() || ""}
                  onValueChange={field.onChange}
                  placeholder="Select building"
                  searchPlaceholder="Search building..."
                  options={buildings.map(b => ({ value: b.id.toString(), label: b.name }))}
                  className="w-full"
                  data-testid="select-apt-building"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="ownerUserId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Property Manager</FormLabel>
              <FormControl>
                <SearchableSelect
                  value={field.value?.toString() || "none"}
                  onValueChange={(val) => field.onChange(val === "none" ? null : Number(val))}
                  placeholder="Select property manager"
                  searchPlaceholder="Search manager..."
                  options={[
                    { value: "none", label: "No Property Manager" },
                    ...owners.filter((o: any) => o.isActive !== false).map(o => ({ value: o.id.toString(), label: o.fullName || o.username })),
                  ]}
                  className="w-full"
                  data-testid="select-apt-owner"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="wifiSsid"
            render={({ field }) => (
              <FormItem>
                <FormLabel>WiFi Name</FormLabel>
                <FormControl><Input {...field} value={field.value || ""} data-testid="input-apt-wifi-ssid" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="wifiPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>WiFi Password</FormLabel>
                <FormControl><Input {...field} value={field.value || ""} data-testid="input-apt-wifi-password" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="checkoutTime"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Checkout Time</FormLabel>
              <FormControl><Input placeholder="11:00 AM" {...field} value={field.value || ""} data-testid="input-apt-checkout-time" /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="fobInstructions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>FOB Return Instructions</FormLabel>
              <FormControl><Textarea {...field} value={field.value || ""} data-testid="input-apt-fob-instructions" /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="aliases"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CSV Import Aliases</FormLabel>
              <FormControl><Input placeholder="Apt 3B, Unit 3-B, Studio Downtown" {...field} value={field.value || ""} data-testid="input-apt-aliases" /></FormControl>
              <p className="text-xs text-muted-foreground">Comma-separated alternative names used to match this apartment during CSV import</p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="heroImage"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Hero Image URL</FormLabel>
              <FormControl><Input placeholder="https://..." {...field} value={field.value || ""} data-testid="input-apt-hero-image" /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bedrooms"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bedrooms</FormLabel>
              <Select onValueChange={(v) => field.onChange(Number(v))} value={String(field.value ?? 1)}>
                <FormControl>
                  <SelectTrigger data-testid="select-apt-bedrooms">
                    <SelectValue placeholder="Select bedrooms" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="0">Studio</SelectItem>
                  <SelectItem value="1">1 Bedroom</SelectItem>
                  <SelectItem value="2">2 Bedrooms</SelectItem>
                  <SelectItem value="3">3 Bedrooms</SelectItem>
                  <SelectItem value="4">4 Bedrooms</SelectItem>
                  <SelectItem value="5">5 Bedrooms</SelectItem>
                  <SelectItem value="6">6 Bedrooms</SelectItem>
                  <SelectItem value="7">7 Bedrooms</SelectItem>
                  <SelectItem value="8">8 Bedrooms</SelectItem>
                  <SelectItem value="9">9 Bedrooms</SelectItem>
                  <SelectItem value="10">10+ Bedrooms</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bathrooms"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bathrooms</FormLabel>
              <Select onValueChange={(v) => field.onChange(Number(v))} value={String(field.value ?? 1)}>
                <FormControl>
                  <SelectTrigger data-testid="select-apt-bathrooms">
                    <SelectValue placeholder="Select bathrooms" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="1">1 Bathroom</SelectItem>
                  <SelectItem value="2">2 Bathrooms</SelectItem>
                  <SelectItem value="3">3 Bathrooms</SelectItem>
                  <SelectItem value="4">4 Bathrooms</SelectItem>
                  <SelectItem value="5">5 Bathrooms</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="maxGuests"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Max Guests</FormLabel>
              <Select onValueChange={(v) => field.onChange(Number(v))} value={String(field.value ?? 2)}>
                <FormControl>
                  <SelectTrigger data-testid="select-apt-max-guests">
                    <SelectValue placeholder="Select max guests" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8,10,12].map(n => (
                    <SelectItem key={n} value={String(n)}>{n} Guest{n !== 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="fobRequired"
          render={({ field }) => (
            <FormItem className="flex items-center gap-3">
              <FormControl>
                <Switch
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                  data-testid="switch-apt-fob-required"
                />
              </FormControl>
              <FormLabel className="!mt-0">FOB Required</FormLabel>
            </FormItem>
          )}
        />

        <div className="border-t pt-4 mt-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            Marketplace Settings
          </h3>

          <FormField
            control={form.control}
            name="marketplaceVisible"
            render={({ field }) => (
              <FormItem className="flex items-center gap-3 mb-3">
                <FormControl>
                  <Switch
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                    data-testid="switch-apt-marketplace-visible"
                  />
                </FormControl>
                <FormLabel className="!mt-0">Show on Marketplace</FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Location</FormLabel>
                <FormControl><Input placeholder="Miami Beach, FL" {...field} value={field.value || ""} data-testid="input-apt-location" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="marketplacePrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nightly Price ($)</FormLabel>
                  <FormControl><Input type="number" step="0.01" placeholder="150.00" {...field} value={field.value || ""} data-testid="input-apt-marketplace-price" /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="marketplaceCleaningFee"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cleaning Fee ($)</FormLabel>
                  <FormControl><Input type="number" step="0.01" placeholder="0.00" {...field} value={field.value ?? ""} data-testid="input-apt-marketplace-cleaning-fee" /></FormControl>
                  <p className="text-xs text-muted-foreground">Overrides the rate plan cleaning fee.</p>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="propertyType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Type</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || "apartment"}>
                  <FormControl>
                    <SelectTrigger data-testid="select-apt-property-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="penthouse">Penthouse</SelectItem>
                    <SelectItem value="loft">Loft</SelectItem>
                    <SelectItem value="cabin">Cabin</SelectItem>
                    <SelectItem value="condo">Condo</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="marketplaceDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Marketplace Description</FormLabel>
                <FormControl><Textarea placeholder="Describe the property for potential guests..." {...field} value={field.value || ""} data-testid="input-apt-marketplace-description" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="amenities"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amenities (comma-separated)</FormLabel>
                <FormControl><Input placeholder="wifi,pool,gym,kitchen,parking,ac" {...field} value={field.value || ""} data-testid="input-apt-amenities" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="border rounded-md p-3 mt-3 bg-accent/30">
            <FormField
              control={form.control}
              name="marketplaceFeatured"
              render={({ field }) => (
                <FormItem className="flex items-center gap-3">
                  <FormControl>
                    <Switch
                      checked={field.value ?? false}
                      onCheckedChange={field.onChange}
                      data-testid="switch-apt-marketplace-featured"
                    />
                  </FormControl>
                  <FormLabel className="!mt-0">Featured Listing (promoted)</FormLabel>
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="border-t pt-4 mt-4">
          <h3 className="text-sm font-semibold mb-3">Tax Override (optional)</h3>
          <p className="text-xs text-muted-foreground mb-3">Leave blank to inherit the org default tax rate. Overrides org-level and rate-plan-level tax for this unit.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="taxRateOverride"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax Rate (%)</FormLabel>
                  <FormControl><Input type="number" step="0.01" min="0" max="100" placeholder="0.00" {...field} value={field.value ?? ""} data-testid="input-apt-tax-rate-override" /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="taxLabelOverride"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax Label</FormLabel>
                  <FormControl><Input placeholder="Sales Tax" {...field} value={field.value || ""} data-testid="input-apt-tax-label-override" /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={isPending} data-testid="button-submit-apartment">
          {isPending ? "Saving..." : submitLabel}
        </Button>
      </form>
    </Form>
  );
}

export default function AdminApartments() {
  const { data: apartments, isLoading } = useApartments();
  const { data: buildings } = useBuildings();
  const { data: users } = useAdminUsers();
  const owners = (users || []).filter((u: any) => u.role === 'owner');
  const createMutation = useCreateApartment();
  const updateMutation = useUpdateApartment();
  const deleteMutation = useDeleteApartment();
  const { data: qaTemplates = [] } = useQaTemplates();
  const applyTemplate = useApplyQaTemplate();
  const { toast } = useToast();
  const [createQaTemplate, setCreateQaTemplate] = useState<string>("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editApartment, setEditApartment] = useState<Apartment | null>(null);

  const search = useSearch();
  const [, navigate] = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const searchQuery = params.get("q") || "";
  const filterBuilding = params.get("building") || "all";
  const filterOwner = params.get("pm") || "all";
  const filterType = params.get("type") || "all";

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    navigate(`/admin/apartments?${next.toString()}`, { replace: true });
  };

  const setSearchQuery = (v: string) => setFilter("q", v);
  const setFilterBuilding = (v: string) => setFilter("building", v);
  const setFilterOwner = (v: string) => setFilter("pm", v);
  const setFilterType = (v: string) => setFilter("type", v);

  const filteredApartments = useMemo(() => {
    if (!apartments) return [];
    return apartments.filter((apt) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const nameMatch = (apt.name || "").toLowerCase().includes(q);
        const slugMatch = (apt.slug || "").toLowerCase().includes(q);
        const locationMatch = (apt.location || "").toLowerCase().includes(q);
        if (!nameMatch && !slugMatch && !locationMatch) return false;
      }
      if (filterBuilding !== "all" && apt.buildingId !== Number(filterBuilding)) return false;
      if (filterOwner !== "all") {
        if (filterOwner === "none" && apt.ownerUserId !== null) return false;
        if (filterOwner !== "none" && apt.ownerUserId !== Number(filterOwner)) return false;
      }
      if (filterType !== "all" && (apt.propertyType || "apartment") !== filterType) return false;
      return true;
    });
  }, [apartments, searchQuery, filterBuilding, filterOwner, filterType]);

  const hasActiveFilters = searchQuery || filterBuilding !== "all" || filterOwner !== "all" || filterType !== "all";

  const clearFilters = () => {
    navigate("/admin/apartments", { replace: true });
  };

  const createDefaults: ApartmentFormValues = {
    name: "",
    slug: "",
    publicToken: Math.random().toString(36).substring(7),
    wifiSsid: "",
    wifiPassword: "",
    buildingId: 0,
    ownerUserId: null,
    checkoutTime: "11:00 AM",
    fobInstructions: "Please leave the fob on the kitchen counter.",
    fobRequired: true,
    heroImage: "",
    bedrooms: 1,
    bathrooms: 1,
    maxGuests: 2,
    location: "",
    marketplaceVisible: false,
    marketplaceDescription: "",
    marketplacePrice: "",
    marketplaceFeatured: false,
    propertyType: "apartment",
    amenities: "",
  };

  async function onCreateSubmit(values: ApartmentFormValues) {
    try {
      const newApt = await createMutation.mutateAsync(values);
      if (createQaTemplate && (newApt as any)?.id) {
        await applyTemplate.mutateAsync({ templateId: Number(createQaTemplate), unitIds: [(newApt as any).id] }).catch(() => {});
      }
      toast({ title: "Apartment created" });
      setCreateOpen(false);
      setCreateQaTemplate("");
    } catch {
      toast({ title: "Error", description: "Failed to create apartment", variant: "destructive" });
    }
  }

  async function onEditSubmit(values: ApartmentFormValues) {
    if (!editApartment) return;
    try {
      await updateMutation.mutateAsync({ id: editApartment.id, data: values });
      toast({ title: "Apartment updated" });
      setEditApartment(null);
    } catch {
      toast({ title: "Error", description: "Failed to update apartment", variant: "destructive" });
    }
  }

  async function handleDelete(id: number) {
    try {
      await deleteMutation.mutateAsync(id);
      toast({ title: "Apartment deleted" });
    } catch {
      toast({ title: "Error", description: "Failed to delete apartment", variant: "destructive" });
    }
  }

  async function regenerateToken(apt: Apartment) {
    const newToken = Math.random().toString(36).substring(2, 14);
    try {
      await updateMutation.mutateAsync({ id: apt.id, data: { publicToken: newToken } });
      toast({ title: "Token regenerated", description: "Guest link has been updated with a new token." });
    } catch {
      toast({ title: "Error", description: "Failed to regenerate token", variant: "destructive" });
    }
  }

  const getGuestLink = (slug: string, token: string) => {
    return `${window.location.origin}/g/${slug}?t=${token}`;
  };

  const getOwnerName = (ownerUserId: number | null) => {
    if (!ownerUserId) return null;
    const owner = owners.find((o: any) => o.id === ownerUserId);
    return owner ? (owner.fullName || owner.username) : "Unknown";
  };

  const getBuildingName = (buildingId: number | null) => {
    if (!buildingId) return "—";
    return buildings?.find(b => b.id === buildingId)?.name || "Unknown";
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Apartments</h1>
          <p className="text-muted-foreground mt-1">
            {apartments ? `${filteredApartments.length} of ${apartments.length} units` : "Loading..."}
          </p>
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-apartment">
              <Plus className="w-4 h-4 mr-2" /> Add Apartment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Apartment</DialogTitle>
            </DialogHeader>
            {(qaTemplates as any[]).length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md border space-y-1">
                <label className="text-xs font-medium text-gray-600 block">QA Zone Template (optional)</label>
                <select
                  className="w-full border rounded-md h-9 px-3 text-sm bg-white"
                  value={createQaTemplate}
                  onChange={e => setCreateQaTemplate(e.target.value)}
                >
                  <option value="">None — auto-generate from bedroom count</option>
                  {(qaTemplates as any[]).map((t: any) => (
                    <option key={t.id} value={t.id}>{t.name} ({(t.zonesJson ?? []).length} zones)</option>
                  ))}
                </select>
              </div>
            )}
            <ApartmentForm
              defaultValues={createDefaults}
              onSubmit={onCreateSubmit}
              isPending={createMutation.isPending}
              submitLabel="Create Apartment"
              buildings={buildings || []}
              owners={owners}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, slug, or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-apartments"
          />
        </div>

        <SearchableSelect
          value={filterBuilding}
          onValueChange={setFilterBuilding}
          placeholder="All Buildings"
          searchPlaceholder="Search building..."
          options={[
            { value: "all", label: "All Buildings" },
            ...(buildings || []).map((b: any) => ({ value: b.id.toString(), label: b.name }))
          ]}
          className="w-[160px] h-9 text-sm"
          data-testid="filter-building"
        />

        <SearchableSelect
          value={filterOwner}
          onValueChange={setFilterOwner}
          placeholder="All Managers"
          searchPlaceholder="Search manager..."
          options={[
            { value: "all", label: "All Managers" },
            { value: "none", label: "Unassigned" },
            ...owners.map((o: any) => ({ value: o.id.toString(), label: `${o.fullName || o.username}${o.isActive === false ? " (inactive)" : ""}` }))
          ]}
          className="w-[180px] h-9 text-sm"
          data-testid="filter-owner"
        />

        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[150px]" data-testid="filter-type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="apartment">Apartment</SelectItem>
            <SelectItem value="house">House</SelectItem>
            <SelectItem value="villa">Villa</SelectItem>
            <SelectItem value="studio">Studio</SelectItem>
            <SelectItem value="penthouse">Penthouse</SelectItem>
            <SelectItem value="loft">Loft</SelectItem>
            <SelectItem value="cabin">Cabin</SelectItem>
            <SelectItem value="condo">Condo</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="button-clear-filters">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="text-center py-12 text-muted-foreground">Loading apartments...</div>
      )}

      {!isLoading && filteredApartments.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          {hasActiveFilters ? "No apartments match your filters." : "No apartments yet. Add one to get started."}
        </div>
      )}

      {!isLoading && filteredApartments.length > 0 && (
        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Building</TableHead>
                <TableHead>Property Manager</TableHead>
                <TableHead className="text-center">Marketplace</TableHead>
                <TableHead className="text-center">Type</TableHead>
                <TableHead className="text-center">Beds</TableHead>
                <TableHead className="text-center">Guests</TableHead>
                <TableHead>Guest Link</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredApartments.map((apt) => (
                <TableRow key={apt.id} data-testid={`row-apartment-${apt.id}`}>
                  <TableCell>
                    <div>
                      <span className="font-medium" data-testid={`text-apt-name-${apt.id}`}>{apt.name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{apt.slug}</span>
                  </TableCell>
                  <TableCell data-testid={`text-apt-building-${apt.id}`}>
                    <span className="text-sm">{getBuildingName(apt.buildingId)}</span>
                  </TableCell>
                  <TableCell data-testid={`text-apt-owner-${apt.id}`}>
                    {getOwnerName(apt.ownerUserId) ? (
                      <span className="text-sm">{getOwnerName(apt.ownerUserId)}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={apt.marketplaceVisible ?? false}
                      onCheckedChange={(checked) => {
                        updateMutation.mutate({ id: apt.id, data: { marketplaceVisible: checked } });
                      }}
                      data-testid={`switch-marketplace-${apt.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex flex-col items-center gap-1">
                      <Badge variant="outline" className="capitalize text-xs no-default-active-elevate">
                        {apt.propertyType || "apartment"}
                      </Badge>
                      {(apt.customCleaningFee && apt.customCleanerPay) && (
                        <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-100 no-default-active-elevate" data-testid={`badge-pricing-override-${apt.id}`}>
                          custom price
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center" data-testid={`text-apt-bedrooms-${apt.id}`}>
                    {apt.bedrooms === 0 ? "S" : apt.bedrooms ?? 1}
                  </TableCell>
                  <TableCell className="text-center">
                    {apt.maxGuests ?? 2}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(getGuestLink(apt.slug, apt.publicToken));
                          toast({ title: "Copied to clipboard" });
                        }}
                        data-testid={`button-copy-link-${apt.id}`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => regenerateToken(apt)}
                        data-testid={`button-regenerate-token-${apt.id}`}
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link href={`/admin/apartments/${apt.id}`}>
                        <Button size="icon" variant="ghost" data-testid={`button-manage-${apt.id}`}>
                          <Settings className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditApartment(apt)}
                        data-testid={`button-edit-apt-${apt.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            data-testid={`button-delete-apt-${apt.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Apartment</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{apt.name}"? This will remove all associated content, checklist items, photo requirements, and submissions.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(apt.id)}
                              data-testid="button-confirm-delete"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!editApartment} onOpenChange={(open) => { if (!open) setEditApartment(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Apartment</DialogTitle>
          </DialogHeader>
          {editApartment && (
            <ApartmentForm
              defaultValues={{
                name: editApartment.name,
                slug: editApartment.slug,
                publicToken: editApartment.publicToken,
                wifiSsid: editApartment.wifiSsid || "",
                wifiPassword: editApartment.wifiPassword || "",
                buildingId: editApartment.buildingId || 0,
                ownerUserId: editApartment.ownerUserId || null,
                checkoutTime: editApartment.checkoutTime || "11:00 AM",
                fobInstructions: editApartment.fobInstructions || "",
                fobRequired: editApartment.fobRequired ?? true,
                heroImage: editApartment.heroImage || "",
                bedrooms: editApartment.bedrooms ?? 1,
                bathrooms: editApartment.bathrooms ?? 1,
                maxGuests: editApartment.maxGuests ?? 2,
                location: editApartment.location || "",
                marketplaceVisible: editApartment.marketplaceVisible ?? false,
                marketplaceDescription: editApartment.marketplaceDescription || "",
                marketplacePrice: editApartment.marketplacePrice || "",
                marketplaceFeatured: editApartment.marketplaceFeatured ?? false,
                propertyType: editApartment.propertyType || "apartment",
                amenities: editApartment.amenities || "",
              }}
              onSubmit={onEditSubmit}
              isPending={updateMutation.isPending}
              submitLabel="Update Apartment"
              buildings={buildings || []}
              owners={owners}
            />
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
